import { Box, Button, Grid, Typography } from '@mui/material'
import React, { useState } from 'react'
import { ROUTES } from '../../helpers/routes-helper'
import { DEFAULT_CARD_IMAGE } from '../../helpers/static-constants'
import GroupBreakdownDialog from '../social/GroupBreakdownDialog'
import useAppUtilities from '../../hooks/useAppUtilities'
import { useDevice } from '../../hooks/useDevice'
import { MuiAlert, MuiSnackbar } from '../../pages/eater/styled/global.styled'
import { useAppSelector } from '../../store'
import {
  selectMessage,
  selectSeverity,
  selectShouldOpen,
  setOpen,
} from '../../store/restaurantSlice'
import { isNeutralMatchLabel } from '../../helpers/restaurant-helpers'
import { RestaurantAndPredictedRestaurant } from '../../types'
import { Member } from '../../types/groupTypes'
import RestaurantCard from './RestaurantCard'

interface RestaurantListProps {
  cardList: RestaurantAndPredictedRestaurant[]
  openMassager?: boolean
  mobileSearchHight: string
  noneMobileSearchHight: string
  groupMode?: boolean
  groupId?: string
  members?: Member[]
  groupName?: string
  // Arrival source threaded into detail-page nav state so restaurant_viewed labels the origin
  // (e.g. 'saved') instead of being derived as a generic in-app view.
  detailSource?: 'saved' | 'in_app'
}

const RestaurantList: React.FC<RestaurantListProps> = ({
  cardList,
  openMassager,
  mobileSearchHight,
  noneMobileSearchHight,
  groupMode = false,
  groupId,
  members = [],
  groupName,
  detailSource,
}) => {
  const { navigate, dispatch } = useAppUtilities()
  const message = useAppSelector(selectMessage)
  const severity = useAppSelector(selectSeverity)
  const openServer = useAppSelector(selectShouldOpen)
  const { isHybrid } = useDevice()
  const [breakdownDialogOpen, setBreakdownDialogOpen] = useState(false)
  const [selectedRestaurant, setSelectedRestaurant] =
    useState<RestaurantAndPredictedRestaurant | null>(null)

  const setOpenServer = (): void => {
    dispatch(setOpen(false))
  }

  const handleShowBreakdown = (
    e: React.MouseEvent,
    restaurant: RestaurantAndPredictedRestaurant,
  ): void => {
    e.stopPropagation()
    setSelectedRestaurant(restaurant)
    setBreakdownDialogOpen(true)
  }

  

  return (
    <Box
      sx={{
        mt: { xs: '16px', md: '40px' },
        mb: { md: '40px' },
        height: {
          xs: `calc(100vh - ${
            isHybrid ? mobileSearchHight : noneMobileSearchHight
          })`,
          md: '100%',
        },
        overflow: 'auto',
      }}
    >
      <Grid container spacing={2}>
        {cardList.map((card, index: number) => (
          <Grid
            key={index}
            item
            xs={12}
            sm={openMassager ? 12 : 6}
            md={openMassager ? 6 : 4}
            lg={4}
          >
            <div
              key={index}
              style={{
                height: '280px',
                width: '100%',
              }}
              className="restaurant-card-swiper-slide"
              onClick={() =>
                navigate(
                  `${ROUTES.RESTAURANT_DETAILS}/${card?.restaurantDTO?.alias}/details`,
                  detailSource ? { state: { source: detailSource } } : undefined,
                )
              }
            >
              <Box sx={{ position: 'relative' }}>
                {groupMode &&
                  card?.group === true &&
                  card.groupRecommendations &&
                  card.groupRecommendations.length > 0 && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: '8px',
                        right: '8px',
                        zIndex: 10,
                      }}
                    >
                      <Button
                        size="small"
                        variant="contained"
                        onClick={(e) => handleShowBreakdown(e, card)}
                        sx={{
                          textTransform: 'none',
                          borderRadius: '8px',
                          fontSize: '12px',
                          px: 1.5,
                          py: 0.5,
                          backgroundColor: 'rgba(255, 255, 255, 0.9)',
                          color: 'primary.main',
                          '&:hover': {
                            backgroundColor: 'rgba(255, 255, 255, 1)',
                          },
                        }}
                      >
                        See breakdown
                      </Button>
                    </Box>
                  )}
                {groupMode &&
                  card?.group === true &&
                  card.recommendation && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: '8px',
                        left: '8px',
                        zIndex: 10,
                        backgroundColor: 'rgba(255, 255, 255, 0.95)',
                        borderRadius: '8px',
                        p: 1,
                        maxWidth: '60%',
                        boxShadow: '0px 2px 8px rgba(0,0,0,0.1)',
                      }}
                    >
                      {!isNeutralMatchLabel(card.recommendation.matchLabel) && (
                        <Typography
                          variant="subtitle2"
                          fontWeight={600}
                          color="primary"
                          sx={{ mb: 0.5 }}
                        >
                          {card.recommendation.matchLabel}
                        </Typography>
                      )}
                      {card.recommendation.summary && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{
                            opacity: 0.9,
                            fontStyle: 'italic',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                          }}
                        >
                          {card.recommendation.summary}
                        </Typography>
                      )}
                    </Box>
                  )}
                <RestaurantCard
                  id={card.restaurantDTO.restaurantId}
                  alias={card?.restaurantDTO?.alias ?? ''}
                  imgList={[
                    card?.restaurantDTO?.imageUrl ??
                    card?.restaurantDTO?.imageUrl !== ''
                      ? card?.restaurantDTO?.imageUrl ?? DEFAULT_CARD_IMAGE
                      : DEFAULT_CARD_IMAGE,
                  ]}
                  title={card.restaurantDTO.name}
                  isVerified={card?.restaurantDTO?.verified ?? false}
                  subTitle={[
                    card?.restaurantDTO?.address1,
                    card?.restaurantDTO?.city,
                    card?.restaurantDTO?.state,
                  ]
                    .filter(Boolean)
                    .join(', ')}
                  distance={`${card?.restaurantDTO?.distance ?? ''}`}
                  tagChipBgColor={'liteOrange'}
                  tagChipData={
                    groupMode && card?.group === true
                      ? [] // In groupMode, hide the standard Match chip, show overlay instead
                      : [
                          // Only render the Match chip when we actually have a numeric score.
                          // An unscored restaurant (no scoreable dishes) sends yes/value = null:
                          // it is UNKNOWN, not a 0% match, so hide the chip entirely (never "0.00%").
                          ...(typeof card.recommendation?.yes === 'number' ||
                          typeof card.value === 'number'
                            ? [
                                {
                                  percentage: `${
                                    typeof card.recommendation?.yes === 'number'
                                      ? card.recommendation.yes.toFixed(2)
                                      : (card.value as number).toFixed(2)
                                  }`,
                                  title: 'Match',
                                },
                              ]
                            : []),
                          ...(card?.group === true && card.groupValue !== undefined
                            ? [
                                {
                                  percentage: `${card.groupValue.toFixed(2)}`,
                                  title: 'Group Match',
                                  matchData:
                                    card.groupRecommendations &&
                                    card.groupRecommendations.length > 0
                                      ? card.groupRecommendations.map((item) => ({
                                          name: item.name ?? '',
                                          email: item?.email ?? '',
                                          image:
                                            'https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcQmJUeQCIV5gK-gudX5l3OIhRcmgnbtGDhExw&s',
                                          percentage: `${(item?.yes ?? 0).toFixed(2)}`,
                                        }))
                                      : undefined,
                                },
                              ]
                            : []),
                        ]
                  }
                />
              </Box>
            </div>
          </Grid>
        ))}
      </Grid>
      <MuiSnackbar open={openServer} onClose={setOpenServer}>
        <MuiAlert onClose={setOpenServer} severity={severity ?? 'success'}>
          {message}
        </MuiAlert>
      </MuiSnackbar>
      {selectedRestaurant && (
        <GroupBreakdownDialog
          open={breakdownDialogOpen}
          onClose={() => {
            setBreakdownDialogOpen(false)
            setSelectedRestaurant(null)
          }}
          restaurantAlias={selectedRestaurant.restaurantDTO.alias}
          groupRecommendations={selectedRestaurant.groupRecommendations}
          groupId={groupId}
          members={members}
          groupName={groupName}
        />
      )}
    </Box>
  )
}

export default RestaurantList
